import moment from 'moment';
import {
  navigateTo,
  getJsonAsync
} from 'shared/actions';

import {
  getAdminShoppingCart,
  setAdminShoppingCart,
  retrieveTournamentInfo
} from '../actions';

import {setTournamentInformation} from '../actionTypes';

import {
  setSelectedDateInState,
  setSquadQuantity,
  setAvailableEvents,
  setNoDatesAvailable,
  displayAvailableSquadDates,
  setActiveTournamentsInState,
  displayVirtualEvents,
  updateBowlerOwnedVirtualEventQuantity,
  updateTeamOwnedVirtualEventQuantity,
  updateTotal,
  updateSquadCountInCart,
  updateBowlerOwnedVirtualEventCountInCart,
  updateTeamOwnedVirtualEventCountInCart,
  displayGuideIfBundled
} from './actionTypes';

import {toastr} from 'react-redux-toastr';

export const handleReviewReservationsClick = () => (dispatch, getState) => {
  const tournamentId = getState().admin.base.tournament.id;

  dispatch(navigateTo(`admin/review/${tournamentId}`));
};

export const updateSquadInLocalStorage = (squadId, quantity) => (dispatch, getState) => {
  const events = getState().admin.schedule.availableEvents;

  let shoppingCart = getAdminShoppingCart(),
    items = shoppingCart.squads.filter(x => x.id === squadId),
    item = items.length ? items[0] : null;

  if (item) {
    item.quantity += quantity;
  } else {
    let squad = null;

    for(let event of events) {
      squad = event.squads.find(({id}) => id === squadId);
      if (squad) {break;}
    }
    if (!squad) {
      throw new Error(`Squad ${squadId} not found in state`);
    }
    shoppingCart.squads.push({
      ...squad,
      quantity
    });
  }

  localStorage.adminCart = JSON.stringify(shoppingCart);
};

export const clearSquadQuantity = squadId => dispatch =>
  dispatch(setSquadQuantity({
    squadId,
    addedToCart: true,
    inputValue: 0,
    max: 0
  }));

export const handleEventSquadAddClick = (squadId, quantity) => dispatch => {
  dispatch(updateSquadCartCount(squadId, quantity));
  dispatch(updateSquadInLocalStorage(squadId, quantity));
  dispatch(updateTotal(getAdminShoppingCart()));
  dispatch(clearSquadQuantity(squadId));
  dispatch(displayGuideIfBundled(squadId));
  toastr.success('Added to Cart!', 'Your selection was added to the cart.');
};

export const retrieveAvailableEvents = selectedDateString => (dispatch, getState) => {
  const tournamentId = getState().admin.base.tournament.id;

  dispatch(getJsonAsync(`admin/tournament/${tournamentId}/reservation/event/squad/available/${selectedDateString}`))
  .then(data => dispatch(setAvailableEvents({
    events: data,
    cart: getAdminShoppingCart()
  })));
};

export const handleDateSelection = selectedDate => dispatch => {
  // infinite-calendar uses moment for selected date
  let formattedDate = selectedDate;

  if (typeof(selectedDate) !== 'string')
    formattedDate = selectedDate.format('YYYY-MM-DD');

  dispatch(retrieveAvailableEvents(formattedDate));
  dispatch(setSelectedDateInState(formattedDate));
}; 

export const retrieveAvailableDates = tournamentId => dispatch => {
  dispatch(getJsonAsync(`admin/tournament/${tournamentId}/reservation/dates`))
  .then(data => {
    if (data) {
      dispatch(displayAvailableSquadDates({
        disabledDates: data.disabledDates.map(item => moment.parseZone(item).format('YYYY-MM-DD')),
        min: moment.parseZone(data.firstAvailableSquadDate).startOf('month').format('YYYY-MM-DD'),
        minDate: moment.parseZone(data.firstAvailableSquadDate).format('YYYY-MM-DD'),
        max: moment.parseZone(data.lastAvailableSquadDate).endOf('month').format('YYYY-MM-DD'),
        maxDate: moment.parseZone(data.lastAvailableSquadDate).format('YYYY-MM-DD'),
        selectedDate: null,
        visible: true
      }));
    } else {
      dispatch(setNoDatesAvailable(true));
    }
  });
};

export const resetScreenToInitialState = () => dispatch => {
  dispatch(displayVirtualEvents({
    virtualEvents: {},
    cart: getAdminShoppingCart()
  }));
  dispatch(displayAvailableSquadDates({visible: false}));
  dispatch(setNoDatesAvailable(false));
  dispatch(setSelectedDateInState(null));
  dispatch(setAvailableEvents({
    events: [],
    cart: getAdminShoppingCart()
  }));
};

export const handleTournamentChange = tournamentId => (dispatch, getState) => {
  const tournament = getState().admin.base.tournament;

  dispatch(resetScreenToInitialState());
  if (tournamentId) {
    // clear admin cart when navigating to another tournament
    if (tournament && tournament.id !== tournamentId) {
      delete localStorage.adminCart;
      dispatch(updateTotal(getAdminShoppingCart()));
    }
    dispatch(retrieveTournamentInfo(tournamentId));
    dispatch(retrieveAvailableDates(tournamentId));
    dispatch(retrieveVirtualEvents(tournamentId));
  } else {
    dispatch(setTournamentInformation(null));
  }
};

export const retrieveVirtualEvents = tournamentId => dispatch => {
  dispatch(getJsonAsync(`tournament/${tournamentId}/virtualevents`))
  .then(virtualEvents => dispatch(displayVirtualEvents({ 
    virtualEvents: virtualEvents,
    cart: getAdminShoppingCart()
  })));
};

export const retrieveActiveTournaments = () => dispatch => {
  dispatch(getJsonAsync('admin/tournament/active'))
  .then(tournaments => {
    const dropDownItems = tournaments
      ? tournaments.map(x => ({value: x.id, label: x.name}))
      : [];

    dispatch(setActiveTournamentsInState(dropDownItems));
  });
};

export const handleBowlerOwnedVirtualEventAddClick = virtualEvent => (dispatch, getState) => {
  let shoppingCart = getAdminShoppingCart();
  const items = shoppingCart.virtualEvents.filter(x => x.id === virtualEvent.id),
    item = items.length ? items[0] : null,
    tournamentId = getState().registration.base.tournamentId;

  if (item) {
    item.quantity += virtualEvent.quantity;
  }
  else {
    shoppingCart.virtualEvents.push(virtualEvent);
  }

  dispatch(updateBowlerOwnedVirtualEventCountCart(virtualEvent.id, virtualEvent.quantity));
  dispatch(setAdminShoppingCart(shoppingCart, tournamentId));
  dispatch(updateTotal(shoppingCart));
  dispatch(updateBowlerOwnedVirtualEventQuantity(virtualEvent.id, 0)); // clear quantity field after adding to cart
  toastr.success('Added to Cart!', 'Your selection was added to the cart.');
};

export const handleTeamOwnedVirtualEventAddClick = virtualEvent => (dispatch, getState) => {
  let shoppingCart = getAdminShoppingCart();
  const items = shoppingCart.virtualEvents.filter(x => x.id === virtualEvent.id),
    item = items.length ? items[0] : null,
    tournamentId = getState().registration.base.tournamentId;
 
  if (item) {
    item.quantity += virtualEvent.quantity;
  }
  else {
    shoppingCart.virtualEvents.push(virtualEvent);
  }

  dispatch(updateTeamOwnedVirtualEventCountCart(virtualEvent.eventId, virtualEvent.id, virtualEvent.quantity));
  dispatch(setAdminShoppingCart(shoppingCart, tournamentId));
  dispatch(updateTotal(shoppingCart));
  dispatch(updateTeamOwnedVirtualEventQuantity(virtualEvent.eventId, virtualEvent.id, 0)); // clear quantity field after adding to cart
  toastr.success('Added to Cart!', 'Your selection was added to the cart.');
};

export const updateSquadCartCount = (squadId, quantity) => dispatch =>
  dispatch(updateSquadCountInCart(squadId, quantity));

export const updateBowlerOwnedVirtualEventCountCart = (virtualEventId, quantity) => dispatch =>
  dispatch(updateBowlerOwnedVirtualEventCountInCart(virtualEventId, quantity));

export const updateTeamOwnedVirtualEventCountCart = (eventId, virtualEventId, quantity) => dispatch =>
  dispatch(updateTeamOwnedVirtualEventCountInCart(eventId, virtualEventId, quantity));


// WEBPACK FOOTER //
// ./src/features/admin/schedule/actions.js